/**
 * Monthlees membership bundle — shared cart helpers for Welcome Kit + member price modal.
 * Sequential /cart/add.js (membership + product). No gift line. No custom pricing math.
 */
(function () {
  'use strict';

  var ADD_SETTLE_MS = 400;

  function formatMoney(cents, moneyFormat) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function normalizeSellingPlanId(value) {
    if (value == null || value === '') return null;
    var str = String(value);
    if (str.indexOf('/') !== -1) {
      str = str.split('/').pop();
    }
    var id = parseInt(str, 10);
    return Number.isFinite(id) ? id : null;
  }

  function normalizeVariantId(value) {
    if (value == null || value === '') return null;
    var str = String(value);
    if (str.indexOf('/') !== -1) {
      str = str.split('/').pop();
    }
    var id = parseInt(str, 10);
    return Number.isFinite(id) ? id : null;
  }

  function parseCartResponse(response) {
    return response.text().then(function (text) {
      var data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = {};
        }
      }

      if (!response.ok) {
        var message =
          (data && (data.description || data.message)) ||
          'Cart request failed (' + response.status + ')';
        throw new Error(message);
      }

      return data;
    });
  }

  function normalizeLineItemProperties(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var props = {};
    Object.keys(raw).forEach(function (key) {
      var val = raw[key];
      if (val == null || val === '') return;
      props[String(key)] = String(val);
    });
    return Object.keys(props).length ? props : null;
  }

  function addCartItem(item) {
    var formData = new FormData();
    formData.append('id', String(item.id));
    formData.append('quantity', String(item.quantity || 1));
    if (item.selling_plan != null && item.selling_plan !== '') {
      formData.append('selling_plan', String(item.selling_plan));
    }
    // Shopify cart/add.js private line props (e.g. properties[_pack_id]=uuid).
    var properties = normalizeLineItemProperties(item.properties);
    if (properties) {
      Object.keys(properties).forEach(function (key) {
        formData.append('properties[' + key + ']', properties[key]);
      });
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(parseCartResponse);
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, ms);
    });
  }

  function addCartItemsSequential(items) {
    var chain = Promise.resolve();

    items.forEach(function (item, index) {
      chain = chain.then(function () {
        return addCartItem(item).then(function () {
          if (index < items.length - 1) {
            return delay(ADD_SETTLE_MS);
          }
          return null;
        });
      });
    });

    return chain;
  }

  function getMembershipProductIds(config) {
    if (config.membershipProductIds && config.membershipProductIds.length) {
      return config.membershipProductIds;
    }
    if (config.membershipProductId) {
      return [config.membershipProductId];
    }
    return [];
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) {
      return res.json();
    });
  }

  function membershipInCart(cart, config) {
    var ids = getMembershipProductIds(config);
    if (!ids.length) return false;
    return (cart.items || []).some(function (item) {
      return ids.some(function (id) {
        return parseInt(id, 10) === item.product_id;
      });
    });
  }

  function buildCartItems(config, productContext, plan) {
    var items = [];
    var errors = [];
    var membershipVariant = null;
    var membershipPlan = null;

    if (plan === 'annual') {
      membershipVariant = normalizeVariantId(config.membershipAnnualVariantId);
      membershipPlan = normalizeSellingPlanId(config.membershipAnnualPlanId);
    } else {
      membershipVariant = normalizeVariantId(config.membershipMonthlyVariantId);
      membershipPlan = normalizeSellingPlanId(config.membershipMonthlyPlanId);
    }

    if (!config.skipMembershipAdd) {
      if (!membershipVariant || !membershipPlan) {
        errors.push(config.missingMembershipLabel || 'Membership is not configured.');
        return { items: items, errors: errors };
      }
      items.push({
        id: membershipVariant,
        quantity: 1,
        selling_plan: membershipPlan,
      });
    }

    var productVariant = normalizeVariantId(productContext.variantId);
    if (!productVariant) {
      errors.push(config.missingProductLabel || 'This product could not be added.');
      return { items: [], errors: errors };
    }

    var productItem = {
      id: productVariant,
      quantity: productContext.quantity > 0 ? productContext.quantity : 1,
    };

    var productPlan = normalizeSellingPlanId(productContext.sellingPlanId);
    if (productContext.requireProductSubscription && !productPlan) {
      errors.push(config.missingProductPlanLabel || 'Subscribe plan is missing for this product.');
      return { items: [], errors: errors };
    }
    if (productPlan) {
      productItem.selling_plan = productPlan;
    }

    // Precision reveal (and other callers) pass pack UUID / tier as private line props.
    var lineProps = normalizeLineItemProperties(productContext.lineItemProperties);
    if (lineProps) {
      productItem.properties = lineProps;
    }

    items.push(productItem);
    return { items: items, errors: errors };
  }

  function publishCartCatalogState(cart, config) {
    var ids = getMembershipProductIds(config || {});
    var hasMembership = membershipInCart(cart, config || {});
    var variantPrices = {};

    (cart.items || []).forEach(function (item) {
      var isMembership = ids.some(function (id) {
        return parseInt(id, 10) === item.product_id;
      });
      if (!isMembership && item.quantity) {
        variantPrices[item.variant_id] = Math.round(item.final_line_price / item.quantity);
      }
    });

    window.MonthleesCartVariantPrices = hasMembership ? variantPrices : {};

    if (document.body) {
      document.body.setAttribute('data-monthlees-membership-in-cart', hasMembership ? 'true' : 'false');
    }
  }

  function isMembershipItem(item, config) {
    var ids = getMembershipProductIds(config || {});
    return ids.some(function (id) {
      return parseInt(id, 10) === item.product_id;
    });
  }

  function changeCartLineQuantity(lineKey, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    }).then(parseCartResponse);
  }

  function clearCart() {
    return fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    }).then(parseCartResponse);
  }

  function lineLooksMemberDiscounted(item) {
    return (
      item &&
      typeof item.original_line_price === 'number' &&
      typeof item.final_line_price === 'number' &&
      item.original_line_price > item.final_line_price
    );
  }

  function needsGuestReprice(cart, config) {
    if (membershipInCart(cart, config || {})) return false;

    return (cart.items || []).some(function (item) {
      return !isMembershipItem(item, config) && lineLooksMemberDiscounted(item);
    });
  }

  function snapshotCartProducts(cart, config) {
    var snapshots = [];

    (cart.items || []).forEach(function (item) {
      if (isMembershipItem(item, config)) return;

      var snap = {
        id: item.variant_id,
        quantity: item.quantity,
      };

      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        snap.selling_plan = normalizeSellingPlanId(item.selling_plan_allocation.selling_plan.id);
      }

      // Preserve Precision `_pack_id` (and any other props) across guest reprice re-adds.
      var snapProps = normalizeLineItemProperties(item.properties);
      if (snapProps) {
        snap.properties = snapProps;
      }

      snapshots.push(snap);
    });

    return snapshots;
  }

  /**
   * Bold keeps member discounts on existing lines after membership is removed.
   * Remove product lines and re-add them so checkout returns to guest pricing.
   * Call only after the membership line has already been removed from cart.
   */
  function repriceAfterMembershipRemoved(config) {
    return fetchCart().then(function (cart) {
      if (membershipInCart(cart, config || {})) {
        return cart;
      }

      var snapshots = snapshotCartProducts(cart, config);
      if (!snapshots.length) {
        publishCartCatalogState(cart, config);
        return cart;
      }

      var productLines = (cart.items || []).filter(function (item) {
        return !isMembershipItem(item, config);
      });

      var chain = Promise.resolve();
      productLines.forEach(function (item) {
        chain = chain.then(function () {
          return changeCartLineQuantity(item.key, 0);
        });
      });

      return chain
        .then(function () {
          return delay(ADD_SETTLE_MS);
        })
        .then(function () {
          return addCartItemsSequential(snapshots);
        })
        .then(function () {
          return delay(ADD_SETTLE_MS);
        })
        .then(fetchCart)
        .then(function (nextCart) {
          publishCartCatalogState(nextCart, config);
          return nextCart;
        });
    });
  }

  function openCartAfterAdd(config) {
    if (config.designMode) {
      window.location.assign(config.cartUrl || '/cart');
      return Promise.resolve();
    }

    if (window.MonthleesCartDrawer && typeof window.MonthleesCartDrawer.refresh === 'function') {
      return window.MonthleesCartDrawer.refresh({ force: true }).then(function () {
        return fetchCart().then(function (cart) {
          publishCartCatalogState(cart, config);
          var hasMembership = membershipInCart(cart, config);
          document.dispatchEvent(
            new CustomEvent('monthlees:cart-membership-toggle', { detail: { active: hasMembership } })
          );
          document.dispatchEvent(new CustomEvent('monthlees:cart-updated'));
        });
      }).then(function () {
        if (typeof window.MonthleesCartDrawer.show === 'function') {
          window.MonthleesCartDrawer.show();
        } else if (typeof window.MonthleesCartDrawer.open === 'function') {
          window.MonthleesCartDrawer.open({ skipFetch: true });
        }
      });
    }

    window.location.assign(config.cartUrl || '/cart');
    return Promise.resolve();
  }

  window.MonthleesMembershipBundle = {
    formatMoney: formatMoney,
    normalizeSellingPlanId: normalizeSellingPlanId,
    normalizeVariantId: normalizeVariantId,
    addCartItemsSequential: addCartItemsSequential,
    buildCartItems: buildCartItems,
    fetchCart: fetchCart,
    membershipInCart: membershipInCart,
    publishCartCatalogState: publishCartCatalogState,
    needsGuestReprice: needsGuestReprice,
    repriceAfterMembershipRemoved: repriceAfterMembershipRemoved,
    openCartAfterAdd: openCartAfterAdd,
  };
})();
